import React, { useEffect, useMemo, useState } from "react";
import { Plus, X } from "lucide-react";
import CalendarMonth from "./CalendarMonth";
import TimePicker from "../screens/TimePicker";

export type TimeRange = { id: string; startTime: string; endTime: string };

export type UnavailableDatesModalProps = {
  onClose: () => void;
  onConfirm: (dateISO: string, ranges: TimeRange[]) => void;
  existingOverrides?: Record<string, TimeRange[]>;
  initialMonth?: Date;
  initialDateISO?: string;
};

const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const newRange = (): TimeRange => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  startTime: "9:00 AM",
  endTime: "5:00 PM",
});

const UnavailableDatesModal: React.FC<UnavailableDatesModalProps> = ({
  onClose,
  onConfirm,
  existingOverrides = {},
  initialMonth,
  initialDateISO,
}) => {
  const [monthDate, setMonthDate] = useState<Date>(initialMonth ?? new Date());

  const initialSelectedDate = useMemo(() => {
    if (initialDateISO) {
      const [y, m, d] = initialDateISO.split("-").map((n) => +n);
      return new Date(y, m - 1, d);
    }
    return new Date();
  }, [initialDateISO]);

  const [selectedDay, setSelectedDay] = useState<number>(
    initialSelectedDate.getDate()
  );

  const selectedDate = useMemo(
    () => new Date(monthDate.getFullYear(), monthDate.getMonth(), selectedDay),
    [monthDate, selectedDay]
  );
  const selectedISO = useMemo(() => toISO(selectedDate), [selectedDate]);

  const [ranges, setRanges] = useState<TimeRange[]>(() => {
    const ex = existingOverrides[selectedISO];
    return ex?.length ? ex.map((r) => ({ ...r })) : [newRange()];
  });

  useEffect(() => {
    const ex = existingOverrides[selectedISO];
    setRanges(ex?.length ? ex.map((r) => ({ ...r })) : [newRange()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedISO]);

  // nested TimePicker
  const [tpOpen, setTpOpen] = useState(false);
  const [tpEditing, setTpEditing] = useState<{
    idx: number;
    end: "start" | "end";
    initial: string;
  } | null>(null);

  const openPicker = (idx: number, end: "start" | "end", initial: string) => {
    setTpEditing({ idx, end, initial });
    setTpOpen(true);
  };
  const onTimePicked = (t: string) => {
    if (!tpEditing) return;
    setRanges((prev) =>
      prev.map((r, i) =>
        i === tpEditing.idx
          ? { ...r, [tpEditing.end === "start" ? "startTime" : "endTime"]: t }
          : r
      )
    );
    setTpOpen(false);
    setTpEditing(null);
  };

  const addRange = () => setRanges((rs) => [...rs, newRange()]);
  const removeRange = (idx: number) =>
    setRanges((rs) => (rs.length <= 1 ? rs : rs.filter((_, i) => i !== idx)));

  const prevMonth = () =>
    setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () =>
    setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const confirm = () => {
    const toMin = (t: string) => {
      const [hm, ap] = t.split(" ");
      const [h, m] = hm.split(":").map(Number);
      let total = (h % 12) * 60 + m;
      if (ap === "PM") total += 12 * 60;
      return total;
    };
    const valid = ranges.filter((r) => toMin(r.startTime) < toMin(r.endTime));
    onConfirm(selectedISO, valid);
  };

  return (
    // overlay (tap outside to dismiss)
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
      onClick={onClose}
    >
      {/* bottom sheet: full width within mobile column, centered horizontally, stuck to bottom */}
      <div
        className="w-full max-w-[400px] bg-[#F5F5F5] rounded-t-lg overflow-hidden relative"
        onClick={(e) => e.stopPropagation()} // keep inside clicks from closing
      >
        {/* grabber bar */}
        <div className="absolute left-1/2 -translate-x-1/2 top-2 w-[60px] h-[3px] bg-[#DEDEDE] opacity-100 rounded-full" />

        {/* Calendar */}
        <div className="pt-5">
          <div className="ds-cal px-5">
            <CalendarMonth
              monthDate={monthDate}
              selectedDays={[selectedDay]}
              onToggleDay={(d) => setSelectedDay(d)}
              onPrevMonth={prevMonth}
              onNextMonth={nextMonth}
            />
          </div>
        </div>

        {/* Editor */}
        <div className="border-t border-[#EAEAEA] bg-[#F7F7F7]">
          <div className="px-5 py-4">
            <div className="text-[12px] text-[#2D2D2D] mb-2">
              What hours are you unavailable?
            </div>

            {ranges.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 mb-3">
                <button
                  className="min-w-[120px] h-[44px] border border-[#CFCFCF] bg-white px-3 text-[14px]"
                  onClick={() => openPicker(i, "start", r.startTime)}
                >
                  {r.startTime}
                </button>
                <button
                  className="min-w-[120px] h-[44px] border border-[#CFCFCF] bg-white px-3 text-[14px]"
                  onClick={() => openPicker(i, "end", r.endTime)}
                >
                  {r.endTime}
                </button>

                {/* bare icons (no border/bg) */}
                <button
                  onClick={() => removeRange(i)}
                  className="p-1 disabled:opacity-40"
                  aria-label="Remove time range"
                  disabled={ranges.length <= 1}
                >
                  <X className="w-4 h-4 text-[#8A8A8A]" />
                </button>

                {i === ranges.length - 1 && (
                  <button onClick={addRange} className="p-1" aria-label="Add time range">
                    <Plus className="w-4 h-4 text-[#8A8A8A]" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Footer (matches TimePicker) */}
          <div className="flex items-center justify-between px-5 pb-5">
            <button
              onClick={onClose}
              className="w-12 h-12 bg-white border border-[#CFCFCF] grid place-items-center"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-[#6B6B6B]" />
            </button>

            <button
              onClick={confirm}
              className="ml-4 h-12 flex-1 bg-[#D62422] hover:brightness-95 active:brightness-90 text-white font-extrabold grid grid-cols-[auto_1fr_auto] items-center px-5"
            >
              <span className="justify-self-start">OK</span>
              <svg className="justify-self-end" width="22" height="22" viewBox="0 0 24 24">
                <path d="M5 12h14M13 5l7 7-7 7" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* nested bottom-sheet time picker */}
      {tpOpen && tpEditing && (
        <div onClick={(e) => e.stopPropagation()}>
          <TimePicker
            initialTime={tpEditing.initial}
            onConfirm={onTimePicked}
            onClose={() => {
              setTpOpen(false);
              setTpEditing(null);
            }}
          />
        </div>
      )}

      {/* flatten day corners inside the calendar */}
      <style>{`
        .ds-cal button,
        .ds-cal [class*="rounded"] {
          border-radius: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default UnavailableDatesModal;
